declare const CONFIG_BASE_URL: string | undefined;
declare const CONFIG_GRAPHQL_URL: string | undefined;
declare const CONFIG_SENTRY_DSN: string | undefined;
declare const CONFIG_AG_GRID_LICENSE: string | undefined;
declare const CONFIG_GTAG_URL: string | undefined;
declare const CONFIG_GA_MEASUREMENT_ID: string | undefined;
declare const CONFIG_ENVIRONMENT_NAME: string | undefined;
declare const CONFIG_PRIVACY_POLICY_URL: string | undefined;
declare const CONFIG_SUTTER_DEMO: string | undefined;
declare const CONFIG_TAB_TRACKING_HOSTS: string | undefined;

// The CONFIG_* globals come from public/config.js, which each deployment
// replaces wholesale. A deployed file may omit a key entirely, leaving the
// identifier undeclared rather than empty -- reading it then throws a
// ReferenceError instead of yielding undefined. Every read goes through this
// helper so a missing global reads as undefined and the fallback applies.
const readGlobal = (read: () => string | undefined): string | undefined => {
  try {
    return read();
  } catch {
    return undefined;
  }
};

// A global wins only when it is present and non-blank; undefined means "not
// configured here, use the build-time env var".
const configured = (read: () => string | undefined): string | undefined => {
  const value = readGlobal(read);
  return value !== undefined && value.trim() !== "" ? value.trim() : undefined;
};

export const BASE_URL: string =
  configured(() => CONFIG_BASE_URL) ??
  (import.meta.env.VITE_BASE_URL as string);

export const GRAPHQL_URL =
  configured(() => CONFIG_GRAPHQL_URL) ??
  (import.meta.env.VITE_GRAPHQL_URL as string);

export const SENTRY_DSN =
  configured(() => CONFIG_SENTRY_DSN) ??
  (import.meta.env.VITE_SENTRY_DSN as string);

export const AG_GRID_LICENSE =
  configured(() => CONFIG_AG_GRID_LICENSE) ??
  (import.meta.env.VITE_AG_GRID_LICENSE as string);

export const GTAG_URL =
  configured(() => CONFIG_GTAG_URL) ??
  (import.meta.env.VITE_GTAG_URL as string);

export const GA_MEASUREMENT_ID =
  configured(() => CONFIG_GA_MEASUREMENT_ID) ??
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string);

export const ENVIRONMENT_NAME =
  configured(() => CONFIG_ENVIRONMENT_NAME) ??
  (import.meta.env.VITE_ENVIRONMENT_NAME as string);

// Comma-separated hostname allowlist. Multi-tab tracking runs only on these
// hosts so dev/qa/staging traffic never pollutes production analytics.
// Entries are reduced to bare hostnames: the gate compares against
// window.location.hostname, so a "https://host/" style value would silently
// never match.
const bareHostname = (value: string): string => {
  let host = value.trim().toLowerCase();
  const schemeEnd = host.indexOf("://");
  if (schemeEnd !== -1) {
    host = host.slice(schemeEnd + 3);
  }
  host = host.split("/")[0];
  const portStart = host.lastIndexOf(":");
  return portStart === -1 ? host : host.slice(0, portStart);
};

export const parseHostList = (raw?: string): string[] =>
  (raw ?? "").split(",").map(bareHostname).filter(Boolean);

// Production hostnames (one deployment, two CNAMEs). Used when the deployed
// config.js omits the key, so a missed deploy config doesn't silently disable
// tracking in production.
const PROD_TAB_TRACKING_HOSTS = "app.constructiviq.com,prod.constructiviq.com";

const rawTabTrackingHosts =
  configured(() => CONFIG_TAB_TRACKING_HOSTS) ??
  ((import.meta.env.VITE_TAB_TRACKING_HOSTS as string) ||
    PROD_TAB_TRACKING_HOSTS);

export const TAB_TRACKING_HOSTS = parseHostList(rawTabTrackingHosts);

export const PRIVACY_POLICY_URL =
  configured(() => CONFIG_PRIVACY_POLICY_URL) ??
  (import.meta.env.VITE_PRIVACY_POLICY_URL as string);

// Unlike the others this is a flag, not a URL: an all-whitespace value is not
// "true", so it is compared without trimming.
const rawSutterDemo = readGlobal(() => CONFIG_SUTTER_DEMO);

export const SUTTER_DEMO: boolean =
  rawSutterDemo !== undefined && rawSutterDemo !== ""
    ? rawSutterDemo === "true"
    : import.meta.env.VITE_SUTTER_DEMO === "true";

export const SUBSCRIPTION_GRAPHQL_URL = `wss://${GRAPHQL_URL}`;
export const GRAPHQL_API_URL = `https://${GRAPHQL_URL}`;
